import React, {useMemo} from 'react';

import {useDataTableSelection} from 'src/components/data-table-selection';
import {SelectedItemsView} from 'src/components/selected-items';

import {Group, Item} from '@pb/kit-component-group';
import {ToolItem, ToolsRibbon, ToolsRibbonGroup} from '@pb/kit-component-tools-ribbon';


import {DeselectAllView} from './deselect-all-view';
import {SelectAllView} from './select-all-view';
import {SelectPageView} from './select-page-view';

import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    totalCount: number,
    rows: Array<number | string>,
    selectionKey: string,
    children?: (ids: Array<number | string>) => React.ReactNode,
    onSetSelection?: (ids: Array<string | number>) => void,
    filtersTools?: React.ReactNode,
    extraTools?: (ids: number[]) => React.ReactNode,
    maxSelectedEntities?: number,
    showSelectPage?: boolean,
    showSelectAll?: boolean,
    getAllEntityIds?: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const ToolsRibbonComponent: React.FC<Props> = props => {
    const {
        totalCount,
        rows,
        selectionKey,
        children,
        onSetSelection,
        extraTools,
        filtersTools,
        showSelectPage,
        getAllEntityIds,
        maxSelectedEntities,
        showSelectAll = true,
    } = props;

    const {
        allIds,
        selection,
        setSelectionAll,
        setSelectionPage,
        deselectAll,
    } = useDataTableSelection({
        rows,
        key: selectionKey,
        onSetSelection,
        getEntityIds: getAllEntityIds,
    });

    const ids = useMemo(() => {
        if (selection.inverted) {
            return (allIds as (string | number)[]).filter((id: string | number) => !selection.ids.includes(id));
        } else {
            return selection.ids;
        }
    }, [selection, allIds]);

    return (
        <ToolsRibbon shadow={true}>
            <Item grow={1}>
                <Group gap="xs">
                    <ToolsRibbonGroup>
                        <ToolItem>
                            <strong>
                                <SelectedItemsView
                                    totalCount={totalCount}
                                    selection={selection}
                                    disabled={false}
                                />
                            </strong>
                        </ToolItem>
                    </ToolsRibbonGroup>
                    <ToolsRibbonGroup>
                        {showSelectAll ? (
                            <SelectAllView
                                getEntityIds={getAllEntityIds}
                                maxSelected={maxSelectedEntities}
                                totalCount={totalCount}
                                selection={selection}
                                setSelection={setSelectionAll}
                            />
                        ) : null}
                        <SelectPageView
                            showSelectPage={showSelectPage}
                            selection={selection}
                            dataRows={rows}
                            setSelection={setSelectionPage}
                        />
                        <DeselectAllView
                            selection={selection}
                            totalCount={totalCount}
                            setSelection={deselectAll}
                        />
                    </ToolsRibbonGroup>
                    {!!children && (selection.inverted ? selection.ids.length !== totalCount : !!selection.ids.length)
                        ? <ToolsRibbonGroup>{children(ids)}</ToolsRibbonGroup>
                        : null}
                    {filtersTools
                        ? <ToolsRibbonGroup>{filtersTools}</ToolsRibbonGroup>
                        : null}
                </Group>
            </Item>
            {!!extraTools && extraTools(ids as number[])}
        </ToolsRibbon>
    );
};
